import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export type TooltipData = {
  content: string;
  rect: DOMRect | null;
  visible: boolean;
};

type Props = {
  content: string;
  rect: DOMRect | null;
  visible: boolean;
};

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

const GrammarTooltip: React.FC<Props> = ({ content, rect, visible }) => {
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!visible || !rect) {
      setStyle({ display: 'none' });
      return;
    }

    const padding = 8;
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const maxWidth = Math.min(480, Math.floor(vw * 0.8));

    // default place above
    const preferredTop = rect.top - padding;
    const tooltipHeightEstimate = 120; // rough
    let top = rect.top - tooltipHeightEstimate - padding;
    let place = 'top';

    if (top < 8) {
      // not enough space above -> place below
      top = rect.bottom + padding;
      place = 'bottom';
    }

    let left = rect.left + rect.width / 2 - maxWidth / 2;
    left = clamp(left, 8, vw - maxWidth - 8);

    setStyle({
      position: 'fixed',
      top: Math.round(top),
      left: Math.round(left),
      maxWidth: maxWidth,
      zIndex: 999999,
      display: 'block',
    });
  }, [rect, visible]);

  if (!visible) return null;

  const tooltip = (
    <div
      role="tooltip"
      aria-hidden={!visible}
      className="grammar-tooltip fixed"
      style={style}
    >
      <div
        className="px-4 py-3 rounded-lg shadow-lg bg-white text-slate-900 border border-slate-200 max-w-full tamil-text dark:bg-slate-900 dark:text-white dark:border-slate-700"
        style={{ lineHeight: 1.6, wordBreak: 'break-word', whiteSpace: 'normal', fontSize: 14 }}
      >
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</div>
      </div>
    </div>
  );

  return createPortal(tooltip, document.body);
};

export default GrammarTooltip;

